import type { ServiceBusHealth } from '../../../types/systemHealth'
import { cn } from '../../../shared/utils/cn'

interface DeadLetterPanelProps {
  data: ServiceBusHealth | undefined
  isLoading: boolean
}

const formatTime = (iso: string): string => {
  const d = new Date(iso)
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}

/**
 * `source` names the stage that gave up on a message, not the pipeline: 'queue'
 * is written by processMessage, which BOTH the Storage Queue and Service Bus
 * consumers call, so it covers Stage-1 ingest on either path. Only 'dlq' (the
 * Service Bus DLQ drainer) and 'poison' (legacy poison recovery) are specific
 * to one pipeline.
 */
const SOURCE_LABELS: Record<string, string> = {
  dlq: 'Service Bus DLQ',
  poison: 'Legacy poison queue',
  queue: 'Stage-1 ingest',
  callback: 'Callback',
  stage2: 'Stage-2 decode',
  // Same device + sequence number already stored with different bytes.
  conflict: 'Sequence conflict',
}

const sourceLabel = (source: string) => SOURCE_LABELS[source] ?? source

export function DeadLetterPanel({ data, isLoading }: DeadLetterPanelProps) {
  const deadLetters = data?.deadLetters

  return (
    <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Dead letters</h2>

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : !deadLetters ? (
        <p className="text-sm text-gray-500">No data.</p>
      ) : (
        <>
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-2">By source</h3>
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-gray-500 border-b border-gray-200">
                <tr>
                  <th className="py-2 pr-4">Source</th>
                  <th className="py-2 pr-4 text-right">Unresolved</th>
                  <th className="py-2 pr-4 text-right">
                    Last {deadLetters.lookbackHours}h
                  </th>
                </tr>
              </thead>
              <tbody>
                {deadLetters.bySource.map((s) => (
                  <tr key={s.source} className="border-b border-gray-100 last:border-0">
                    <td className="py-2 pr-4">
                      {sourceLabel(s.source)}{' '}
                      <span className="font-mono text-xs text-gray-400">{s.source}</span>
                    </td>
                    <td
                      className={cn(
                        'py-2 pr-4 text-right tabular-nums',
                        s.unresolved > 0 && 'text-red-700 font-medium'
                      )}
                    >
                      {s.unresolved}
                    </td>
                    <td className="py-2 pr-4 text-right tabular-nums">{s.recent}</td>
                  </tr>
                ))}
                {deadLetters.bySource.length === 0 && (
                  <tr>
                    <td colSpan={3} className="py-3 text-center text-gray-500">
                      No dead letters recorded.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">Most recent</h3>
            <table className="w-full text-sm table-fixed">
              <thead className="text-left text-xs uppercase text-gray-500 border-b border-gray-200">
                <tr>
                  <th className="py-2 pr-4 w-32">Time</th>
                  <th className="py-2 pr-4 w-20">Source</th>
                  <th className="py-2 pr-4 w-24">Device</th>
                  <th className="py-2 pr-4 w-16 text-right">Seq</th>
                  <th className="py-2 pr-4 w-20 text-right">Deliveries</th>
                  <th className="py-2">Error</th>
                </tr>
              </thead>
              <tbody>
                {deadLetters.latest.map((r) => (
                  <tr key={r.id} className="border-b border-gray-100 last:border-0">
                    <td className="py-2 pr-4 font-mono text-gray-700">{formatTime(r.lastSeen)}</td>
                    <td className="py-2 pr-4 font-mono text-gray-700">{r.source}</td>
                    <td className="py-2 pr-4 font-mono text-gray-700">{r.sigfoxId ?? '—'}</td>
                    <td className="py-2 pr-4 text-right tabular-nums">{r.sigfoxSnum ?? '—'}</td>
                    <td className="py-2 pr-4 text-right tabular-nums">{r.deliveryCount ?? '—'}</td>
                    <td className="py-2 truncate text-gray-700" title={r.errorDetail ?? ''}>
                      {r.errorDetail ?? r.errorClass ?? '—'}
                    </td>
                  </tr>
                ))}
                {deadLetters.latest.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-3 text-center text-gray-500">
                      No dead letters recorded.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  )
}
